import { Button, Icon, Modal, Toast, type ToastType } from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  fulfilmentDashboardService,
  type FulfilmentDashboardResponse,
  type DashboardWarning,
  type RegionGroup,
  type CountryGroup,
  type RegionSummary,
  type DashboardSubscription,
  type FeatureRollupEntry,
  type FeatureRollupSubscriptionRef,
} from "../../../api/fulfilmentDashboard.service";
import EmptyState from "../../../components/EmptyState";
import LoadingState from "../../../components/LoadingState";
import { PageHeader } from "../../../components/PageHeader";
import { ROUTES } from "../../../routes";
import { formatDateShort } from "../../../utils/dateFormatter";
import styles from "./FirmDashboard.module.css";

function resolveSubscriptionStatus(subscription: DashboardSubscription): string {
  if (subscription.status === "cancelled") return "cancelled";
  const endDate = new Date(subscription.endDate);
  const now = new Date();
  if (endDate < now) return "expired";
  const daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (daysRemaining <= 30) return "expiring";
  return "active";
}

function SummaryBadges({ summary }: { summary: RegionSummary }) {
  const hasActive = summary.active > 0 || summary.expiringSoon > 0;
  return (
    <div className={styles.summaryBadges}>
      {summary.active > 0 && (
        <span className={styles.summaryBadge} data-status="active">
          {summary.active} active
        </span>
      )}
      {summary.expiringSoon > 0 && (
        <span className={styles.summaryBadge} data-status="expiring">
          {summary.expiringSoon} expiring
        </span>
      )}
      {!hasActive && (
        <span className={styles.summaryBadge} data-status="none">
          no active
        </span>
      )}
    </div>
  );
}

function SubscriptionDetail({ subscription }: { subscription: DashboardSubscription }) {
  const status = resolveSubscriptionStatus(subscription);

  return (
    <div className={styles.subscriptionItem}>
      <span className={styles.subscriptionPackage}>
        {subscription.packageName ?? "Unknown package"}
      </span>
      <span className={styles.statusChip} data-status={status}>
        {status}
      </span>
      <span className={styles.subscriptionDates}>
        {formatDateShort(subscription.startDate)} — {formatDateShort(subscription.endDate)}
      </span>
      {subscription.correlationId && (
        <span className={styles.subscriptionCorrelation}>{subscription.correlationId}</span>
      )}
      <div className={styles.subscriptionActions}>
        <Link
          to={ROUTES.fulfilment.subscriptionDetail(subscription.id)}
          onClick={e => e.stopPropagation()}
        >
          <Button variant="outlined" size="small">
            Edit
          </Button>
        </Link>
      </div>
    </div>
  );
}

function CountrySection({ country, isDuplicate }: { country: CountryGroup; isDuplicate: boolean }) {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  return (
    <div className={styles.countryRow}>
      <div
        className={styles.countryHeader}
        onClick={toggleExpanded}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleExpanded();
          }
        }}
      >
        <div className={styles.countryHeaderLeft}>
          <Icon
            icon="chevron-right"
            size="small"
            className={styles.expandIcon}
            data-expanded={expanded}
          />
          <span className={styles.countryName}>{country.name}</span>
          {isDuplicate && (
            <span className={styles.summaryBadge} data-status="duplicate">
              duplicate subscriptions
            </span>
          )}
        </div>
        <SummaryBadges summary={country.summary} />
      </div>
      <div className={styles.countryBody} data-expanded={expanded}>
        <div className={styles.countryBodyInner}>
          {country.subscriptions.filter(s => {
            const status = resolveSubscriptionStatus(s);
            return status === "active" || status === "expiring";
          }).length === 0 ? (
            <div className={styles.emptySubscriptions}>No active subscriptions</div>
          ) : (
            <div className={styles.subscriptionList}>
              {country.subscriptions
                .filter(s => {
                  const status = resolveSubscriptionStatus(s);
                  return status === "active" || status === "expiring";
                })
                .map(subscription => (
                  <SubscriptionDetail key={subscription.id} subscription={subscription} />
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RegionCard({
  region,
  duplicateSiteStructureIds,
}: {
  region: RegionGroup;
  duplicateSiteStructureIds: Set<string>;
}) {
  const [expanded, setExpanded] = useState(false);
  const isGlobal = region.slug === "global";

  const toggleExpanded = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  const allSubscriptions = useMemo(
    () => region.countries.flatMap(c => c.subscriptions),
    [region.countries],
  );

  const regionHasDuplicates = region.countries.some(c =>
    duplicateSiteStructureIds.has(c.siteStructureId),
  );

  if (isGlobal) {
    return (
      <div className={styles.regionCard}>
        <div className={styles.regionHeader}>
          <div className={styles.regionHeaderLeft}>
            <span className={styles.regionName}>{region.name}</span>
          </div>
          <SummaryBadges summary={region.summary} />
        </div>
        <div className={styles.regionBody} data-expanded={true}>
          <div className={styles.regionBodyInner}>
            {allSubscriptions.filter(s => {
              const status = resolveSubscriptionStatus(s);
              return status === "active" || status === "expiring";
            }).length === 0 ? (
              <div className={styles.emptySubscriptions}>No active subscriptions</div>
            ) : (
              <div className={styles.subscriptionList}>
                {allSubscriptions
                  .filter(s => {
                    const status = resolveSubscriptionStatus(s);
                    return status === "active" || status === "expiring";
                  })
                  .map(subscription => (
                    <SubscriptionDetail key={subscription.id} subscription={subscription} />
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.regionCard}>
      <div
        className={styles.regionHeader}
        onClick={toggleExpanded}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleExpanded();
          }
        }}
      >
        <div className={styles.regionHeaderLeft}>
          <Icon
            icon="chevron-right"
            size="small"
            className={styles.expandIcon}
            data-expanded={expanded}
          />
          <span className={styles.regionName}>{region.name}</span>
          {regionHasDuplicates && (
            <span className={styles.summaryBadge} data-status="duplicate">
              duplicate subscriptions
            </span>
          )}
        </div>
        <SummaryBadges summary={region.summary} />
      </div>
      <div className={styles.regionBody} data-expanded={expanded}>
        <div className={styles.regionBodyInner}>
          {region.countries.map(country => (
            <CountrySection
              key={country.siteStructureId}
              country={country}
              isDuplicate={duplicateSiteStructureIds.has(country.siteStructureId)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const FEATURE_LABELS: Record<string, string> = {
  live: "Legal 500 Live",
  sars: "SARS Data",
  referee_track: "Referee Tracking",
  submissions: "Submissions",
  convert_now: "Convert Now",
  profiles: "Profiles",
  nominations: "Nominations",
  historical_data: "Historical Data",
};

function humanizeFeatureCode(feature: string): string {
  return feature
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function featureLabel(feature: string): string {
  return FEATURE_LABELS[feature] ?? humanizeFeatureCode(feature);
}

function FeatureStateBadge({ entry }: { entry: FeatureRollupEntry }) {
  if (entry.state === "current") {
    return (
      <span className={styles.summaryBadge} data-status="active">
        current{entry.until ? ` — until ${formatDateShort(entry.until)}` : ""}
      </span>
    );
  }
  if (entry.state === "future") {
    return (
      <span className={styles.summaryBadge} data-status="expiring">
        future{entry.from ? ` — from ${formatDateShort(entry.from)}` : ""}
      </span>
    );
  }
  return (
    <span className={styles.summaryBadge} data-status="none">
      not yet entitled
    </span>
  );
}

function FeatureSubscriptionRefItem({
  subscriptionRef,
  feature,
}: {
  subscriptionRef: FeatureRollupSubscriptionRef;
  feature: string;
}) {
  const isPending = (subscriptionRef.featuresNeedingConfiguration ?? []).includes(feature);
  return (
    <div className={styles.featureSubscriptionRef}>
      <span
        className={styles.statusChip}
        data-status={isPending ? "pending" : subscriptionRef.resolvedStatus}
      >
        {isPending ? "pending" : subscriptionRef.resolvedStatus}
      </span>
      <span className={styles.featureSubscriptionSite}>
        {subscriptionRef.siteStructureId ?? "Unscoped"}
      </span>
      <Link
        to={ROUTES.fulfilment.subscriptionDetail(subscriptionRef.id)}
        className={styles.featureSubscriptionLink}
      >
        {isPending ? "Configure subscription" : "View subscription"}
      </Link>
    </div>
  );
}

function FeatureRow({ entry }: { entry: FeatureRollupEntry }) {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  return (
    <div className={styles.featureRow}>
      <div
        className={styles.featureRowHeader}
        onClick={toggleExpanded}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleExpanded();
          }
        }}
      >
        <div className={styles.featureRowHeaderLeft}>
          <Icon
            icon="chevron-right"
            size="small"
            className={styles.expandIcon}
            data-expanded={expanded}
          />
          <span className={styles.featureName}>{featureLabel(entry.feature)}</span>
          {entry.requiresConfiguration && (
            <span className={styles.summaryBadge} data-status="pending">
              needs configuration
            </span>
          )}
        </div>
        <div className={styles.featureRowHeaderRight}>
          {entry.publicationIds.length > 0 && (
            <span className={styles.publicationCount}>
              {entry.publicationIds.length} publication
              {entry.publicationIds.length === 1 ? "" : "s"}
            </span>
          )}
          <FeatureStateBadge entry={entry} />
        </div>
      </div>
      <div className={styles.featureRowBody} data-expanded={expanded}>
        <div className={styles.featureRowBodyInner}>
          {entry.publicationIds.length > 0 && (
            <div className={styles.publicationIdsList}>
              <span className={styles.featureDetailLabel}>Effective publication ids:</span>
              {entry.publicationIds.map(publicationId => (
                <span key={publicationId} className={styles.subscriptionCorrelation}>
                  {publicationId}
                </span>
              ))}
            </div>
          )}
          <div className={styles.featureSubscriptionRefs}>
            <span className={styles.featureDetailLabel}>Supplied by:</span>
            {entry.subscriptions.length === 0 ? (
              <span className={styles.emptySubscriptions}>
                No subscription found — likely a default baseline package.
              </span>
            ) : (
              entry.subscriptions.map(subscriptionRef => (
                <FeatureSubscriptionRefItem
                  key={subscriptionRef.id}
                  subscriptionRef={subscriptionRef}
                  feature={entry.feature}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureProductsOverview({ features }: { features: FeatureRollupEntry[] }) {
  if (features.length === 0) {
    return (
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Firm Products (from Fulfilment)</h2>
        <p>No fulfilment features found for this firm.</p>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Firm Products (from Fulfilment)</h2>
      <p className={styles.sectionSubtitle}>
        Read-only. Each row is the effective, consolidated state across all of this firm&apos;s
        subscriptions for that product — edit configuration on the contributing subscription(s)
        instead.
      </p>
      {features.map(entry => (
        <FeatureRow key={entry.feature} entry={entry} />
      ))}
    </div>
  );
}

function RegionalOverview({
  regions,
  duplicateSiteStructureIds,
}: {
  regions: RegionGroup[];
  duplicateSiteStructureIds: Set<string>;
}) {
  if (regions.length === 0) {
    return (
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Subscriptions by Region</h2>
        <p>No subscriptions found for this firm.</p>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Subscriptions by Region</h2>
      {regions.map(region => (
        <RegionCard
          key={region.slug}
          region={region}
          duplicateSiteStructureIds={duplicateSiteStructureIds}
        />
      ))}
    </div>
  );
}

export default function FirmDashboard() {
  const { firmId: firmIdParam } = useParams<{ firmId: string }>();
  const navigate = useNavigate();
  const firmId = firmIdParam ?? "";

  const [dashboard, setDashboard] = useState<FulfilmentDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const duplicateSiteStructureIds = useMemo(() => {
    const ids = new Set<string>();
    if (dashboard) {
      for (const warning of dashboard.warnings) {
        if (warning.type === "duplicate" && warning.siteStructureId) {
          ids.add(warning.siteStructureId);
        }
      }
    }
    return ids;
  }, [dashboard]);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fulfilmentDashboardService.getDashboard(firmId);
      setDashboard(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [firmId]);

  useEffect(() => {
    if (firmId.length > 0) {
      loadDashboard();
    }
  }, [firmId, loadDashboard]);

  const handleRefreshPages = useCallback(async () => {
    try {
      setRefreshing(true);
      await fulfilmentDashboardService.refreshPages(firmId);
      setShowRefreshModal(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to refresh pages";
      setToast({ message, type: "error" });
    } finally {
      setRefreshing(false);
    }
  }, [firmId]);

  const handleNavigateToCreate = useCallback(() => {
    navigate(`${ROUTES.fulfilment.subscriptions}?firmSearch=${firmId}`);
  }, [navigate, firmId]);

  const handleBackClick = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(ROUTES.fulfilment.subscriptions);
    }
  }, [navigate]);

  if (firmId.length === 0) {
    return (
      <EmptyState
        variant="error"
        title="Invalid firm ID"
        message="The firm ID in the URL is not valid."
      />
    );
  }

  if (loading) {
    return <LoadingState message="Loading firm dashboard..." />;
  }

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load dashboard"
        message={error.message}
        action={{ label: "Retry", onClick: loadDashboard }}
      />
    );
  }

  if (!dashboard) {
    return <EmptyState variant="empty" title="No data" message="No dashboard data available." />;
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader
          title={dashboard.firmName}
          subtitle="Fulfilment dashboard — subscriptions by region"
        >
          <div className={styles.headerActions}>
            <Button variant="outlined" color="default" onClick={handleBackClick}>
              <Icon icon="arrow-left" size="small" />
              Back
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleRefreshPages}
              disabled={refreshing}
            >
              <Icon icon="settings-cog" size="small" />
              {refreshing ? "Refreshing..." : "Refresh Pages"}
            </Button>
            <Button variant="contained" color="primary" onClick={handleNavigateToCreate}>
              <Icon icon="plus" size="small" />
              Create Subscription
            </Button>
          </div>
        </PageHeader>
      </div>

      <FeatureProductsOverview features={dashboard.features} />

      <RegionalOverview
        regions={dashboard.regions}
        duplicateSiteStructureIds={duplicateSiteStructureIds}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}

      <Modal
        isOpen={showRefreshModal}
        onClose={() => setShowRefreshModal(false)}
        title="Page Refresh Triggered"
        size="small"
        blurBackground
      >
        <p>
          A page refresh has been triggered for this firm. The site pages will be regenerated in the
          background — this may take a few minutes to complete.
        </p>
        <p>
          You can continue working while the pages update. Check the site after a few minutes to
          verify the changes are reflected.
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
          <Button variant="contained" color="primary" onClick={() => setShowRefreshModal(false)}>
            OK
          </Button>
        </div>
      </Modal>
    </div>
  );
}
